import {
    ChangeEvent,
    useEffect,
    useRef,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import classnames from 'classnames'

import Map, {
    DEFAULT_LAT,
    DEFAULT_LNG,
    MapMarker,
    ZOOM_LEVEL,
} from './map'
import Loader from './loader'

interface AutocompleteRawProps {
    onPlaceSelected: (place: google.maps.places.PlaceResult) => void
    placeholder?: string
    className?: string
    locationTypes?: string[]
    type?: string
    value?: string
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void
}

export const AutocompleteRaw = ({
    onPlaceSelected,
    placeholder,
    className,
    locationTypes,
    type,
    value,
    onChange,
}: AutocompleteRawProps) => {
    const placesLib = useMapsLibrary('places')
    const inputRef = useRef<HTMLInputElement | null>(null)
    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
    const onPlaceSelectedRef = useRef(onPlaceSelected)
    onPlaceSelectedRef.current = onPlaceSelected

    useEffect(() => {
        if (!placesLib || !inputRef.current) {
            return
        }

        const autocomplete = new placesLib.Autocomplete(inputRef.current, {
            types: locationTypes && locationTypes.length > 0 ? locationTypes : undefined,
        })

        autocompleteRef.current = autocomplete

        const listener = autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace()
            onPlaceSelectedRef.current(place)
        })

        return () => {
            listener.remove()
            autocompleteRef.current = null
        }
    }, [placesLib, locationTypes])

    const resolvedClassName = className || classnames({
        'form-control': true,
        'd-block': type === 'user_defined',
        'd-none': type !== 'user_defined',
    })

    const controlledProps = value !== undefined && onChange !== undefined
        ? { value, onChange }
        : {}

    return (
        <input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            className={resolvedClassName}
            {...controlledProps}
        />
    )
}

interface AutocompleteRowProps {
    type?: string
    handleLocationChange: (place: google.maps.places.PlaceResult) => void
}

const AutocompleteRow = ({ type, handleLocationChange }: AutocompleteRowProps) => {
    const { t } = useTranslation()
    const placesLib = useMapsLibrary('places')

    if (!placesLib) {
        return (
            <div className="form-group">
                <Loader />
            </div>
        )
    }

    return (
        <div className="form-group">
            <label
                className={classnames({
                    'd-block': type === 'user_defined',
                    'd-none': type !== 'user_defined',
                })}
            >
                {t('locationHelperText')}
                {' '}
                <small className="text-danger">{t('requiredInBrackets')}</small>
            </label>
            <AutocompleteRaw
                type={type}
                onPlaceSelected={place => handleLocationChange(place)}
                placeholder={t('autoCompleteLocationPlaceholder')}
            />
        </div>
    )
}

export default AutocompleteRow

interface MapRowProps {
    marker?: MapMarker | MapMarker[] | null
    onMapClick?: Parameters<typeof Map>[0]['onMapClick']
    scrollwheel?: boolean
}

export const MapRow = ({ marker, onMapClick, scrollwheel }: MapRowProps) => {
    const markers: MapMarker[] | null = marker
        ? (Array.isArray(marker) ? marker : [marker])
        : null

    return (
        <Map
            markers={markers}
            options={{
                center: {
                    lat: DEFAULT_LAT,
                    lng: DEFAULT_LNG,
                },
                zoom: ZOOM_LEVEL,
                scrollwheel: scrollwheel ?? false,
                mapTypeControl: false,
                fullscreenControl: true,
                streetViewControl: false,
            }}
            fitBounds={true}
            boundsSize={0.0005}
            style={{ minHeight: 200 }}
            onMapClick={onMapClick}
        />
    )
}
